import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError } from '@cortex/faro';
import { config } from '../config.js';
import type {
  CurrentUserResponse,
  LineOfficialAccountsResponse,
  ProfileLookupRequest,
  ProfileLookupSummary,
  ResolveRequest,
  ResolveSummary,
} from '../types/api.js';

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Engineering
 * @graph-business APIエラークラス。HTTPステータス・エラーコード付きのカスタムError
 * @graph-connects none
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
  };
}

/**
 * 汎用フェッチラッパー
 * @graph-stack line-user-id-resolver
 * @graph-domain Engineering
 * @graph-business line-user-id-resolver Web フロントエンドの汎用 fetch ラッパー。authApi / resolverApi から呼ばれ、401 ハンドリング（X-Edge-Auth-Start ヘッダ検出と /login リダイレクト）と JSON パースを共通化する。個別エンドポイントは呼び出し元（authApi / resolverApi）側で宣言済み
 * @graph-connects line-user-id-resolver-api [calls] line-user-id-resolver API への HTTP リクエスト共通入口
 */
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers ?? {}),
    ),
  };

  const res = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    if (res.headers.get('X-Edge-Auth-Start')) {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
    } else if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!res.ok) {
    const err = (await res.json().catch((error: unknown) => {
      // エラーレスポンス本体が JSON でない場合は HTTP ステータスのみで報告する
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'error_response_body_parse_failed',
      });
      return {} as ErrorResponse;
    })) as ErrorResponse;
    throw new ApiError(
      err.error?.message ?? `HTTP ${String(res.status)}`,
      res.status,
      err.error?.code,
    );
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Engineering
 * @graph-business 認証APIクライアント。/auth/me と /auth/logout エンドポイントを呼び出し
 * @graph-connects lineUserIdResolverAuthApi [calls] 認証 API ApiEndpoint（GET /auth/me, POST /auth/logout）を呼び出し
 */
export const authApi = {
  getCurrentUser: () =>
    request<CurrentUserResponse>('/auth/me').catch((error: unknown) => {
      // 認証 API 失敗時は未ログイン扱い。UI 側で /login へリダイレクトする
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'get_current_user_failed',
      });
      return null;
    }),
  logout: () => request<undefined>('/auth/logout', { method: 'POST' }),
};

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Marketing, Operations
 * @graph-business resolverApi の失敗を Grafana Faro へ集約する内部ヘルパー。LINE user ID / arthur_user_id / CSV 本文は context に含めない（reveal データ漏洩防止）
 * @graph-connects faro [writes_to] resolverApi の失敗を Grafana Faro へ送信
 */
function logResolverApiError(
  event: string,
  error: unknown,
  context: Record<string, unknown>,
): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const apiError = error instanceof ApiError ? error : null;
  logError(err, {
    event,
    ...(apiError
      ? { httpStatus: String(apiError.status), errorCode: apiError.code ?? 'unknown' }
      : {}),
    ...context,
  });
}

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Marketing, Operations
 * @graph-business resolve / profile API クライアント。LINE 公式アカウント一覧取得、CSV 突合、プロフィール lookup の3エンドポイントを提供
 * @graph-connects lineUserIdResolverResolveApi [calls] resolve API ApiEndpoint（GET /accounts, POST /resolve）を呼び出し
 * @graph-connects lineUserIdResolverProfileApi [calls] profile API ApiEndpoint（POST /profile）を呼び出し
 */
export const resolverApi = {
  getAccounts: (): Promise<LineOfficialAccountsResponse> =>
    request<LineOfficialAccountsResponse>('/line-user-id-resolver/accounts'),
  resolve: (input: ResolveRequest): Promise<ResolveSummary> =>
    request<ResolveSummary>('/line-user-id-resolver/resolve', {
      method: 'POST',
      body: JSON.stringify(input),
    }).catch((error: unknown) => {
      // csv / arthurUserIds / matchedPairs は絶対にログに含めない（reveal データ漏洩防止）
      logResolverApiError('resolve_api_failed', error, {
        lineOfficialAccountId: input.lineOfficialAccountId,
      });
      throw error;
    }),
  lookupProfile: (input: ProfileLookupRequest): Promise<ProfileLookupSummary> =>
    request<ProfileLookupSummary>('/line-user-id-resolver/profile', {
      method: 'POST',
      body: JSON.stringify(input),
    }).catch((error: unknown) => {
      // lineUserId は絶対にログに含めない（reveal データ漏洩防止）
      logResolverApiError('profile_lookup_api_failed', error, {
        lineOfficialAccountId: input.lineOfficialAccountId,
      });
      throw error;
    }),
};
